import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Home } from "lucide-react";
import { adminService } from "@/services/adminService";
import type { AdminHouseListRow } from "@/services/adminService";
import {
  countByStatusFilter,
  matchesStatusFilter,
  type StatusFilter,
} from "@/components/admin/adminListStatus";
import {
  DataTable,
  DataTablePrimaryText,
  type DataTableColumn,
} from "@/components/table";
import { AdminInactiveStatusDot } from "@/components/admin/AdminInactiveStatusDot";
import { AdminStatusFilter } from "@/components/admin/AdminStatusFilter";
import { matchesTextQuery } from "@/lib/textSearch";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import { PageFrame, AdminListToolbar, buildListResetToken } from "@/components/common";
import {
  AdminRegionFilter,
  type AdminRegionFilterValue,
} from "@/components/admin/AdminRegionFilter";
import { RegionCodeBadge } from "@/components/admin/RegionCodeBadge";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui";

type SortColumn = "name" | "owner" | "region" | "commission";

export default function AdminStructuresPage() {
  const { t } = useTranslation("adminStructures");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [regionFilter, setRegionFilter] =
    useState<AdminRegionFilterValue>(null);

  const {
    data: structures = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin", "structures", regionFilter],
    queryFn: () =>
      adminService.getHouses({ region_id: regionFilter ?? undefined }),
  });

  const searchMatched = useMemo(() => {
    if (!searchQuery.trim()) return structures;
    return structures.filter((structure) =>
      matchesTextQuery(searchQuery, [
        structure.name,
        structure.owner_name,
        structure.address,
      ]),
    );
  }, [structures, searchQuery]);

  const filteredStructures = useMemo(
    () =>
      searchMatched.filter((structure) =>
        matchesStatusFilter(structure.is_active, statusFilter),
      ),
    [searchMatched, statusFilter],
  );

  const statusFilterCounts = useMemo(
    () =>
      countByStatusFilter(searchMatched, (structure) => structure.is_active),
    [searchMatched],
  );

  const columns = useMemo<DataTableColumn<AdminHouseListRow, SortColumn>[]>(
    () => [
      {
        id: "name",
        header: t("columns.name"),
        sortKey: "name",
        sortKind: "text",
        getSortValue: (row) => row.name,
        defaultWidth: 360,
        cell: (structure) => (
          <span className="flex min-w-0 items-center gap-3">
            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
                structure.is_active
                  ? "bg-primary-50 text-primary-600 ring-primary-600/15"
                  : "bg-surface-100 text-surface-400 ring-surface-300/60",
              )}
              aria-hidden
            >
              <Home className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="flex min-w-0 items-center gap-1.5">
                <DataTablePrimaryText className="truncate">
                  {structure.name}
                </DataTablePrimaryText>
                {structure.is_active ? null : <AdminInactiveStatusDot />}
              </span>
              <span
                className="mt-0.5 block truncate text-xs text-surface-500"
                title={structure.address ?? undefined}
              >
                {structure.address || t("noAddress")}
              </span>
            </span>
          </span>
        ),
      },
      {
        id: "owner",
        header: t("columns.owner"),
        sortKey: "owner",
        sortKind: "text",
        getSortValue: (row) => row.owner_name,
        defaultWidth: 220,
        cell: (structure) => (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/hosts/${structure.owner_user_id}`);
            }}
            className="group inline-flex max-w-full items-center gap-2 text-left"
            title={structure.owner_name}
          >
            <Avatar
              size="sm"
              name={structure.owner_name}
              avatarLabel={structure.owner_name}
              className="h-6 w-6 text-[10px]"
            />
            <span className="truncate text-primary-700 group-hover:text-primary-800 group-hover:underline">
              {structure.owner_name}
            </span>
          </button>
        ),
      },
      {
        id: "region",
        header: tCommon("region.colCode"),
        sortKey: "region",
        sortKind: "text",
        getSortValue: (row) => row.region_code,
        defaultWidth: 100,
        cell: (structure) => <RegionCodeBadge code={structure.region_code} />,
      },
      {
        id: "commission",
        header: t("columns.commission"),
        sortKey: "commission",
        sortKind: "number",
        getSortValue: (row) => row.commission_rate,
        defaultWidth: 120,
        cell: (structure) => (
          <span className="tabular-nums text-surface-700">
            {formatCommissionPercentLabel(structure.commission_rate)}
          </span>
        ),
      },
    ],
    [navigate, t, tCommon],
  );

  return (
    <PageFrame title={t("title")} description={t("subtitle")} icon={Home}>
      <AdminListToolbar
        search={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t("searchPlaceholder")}
        filterChildren={
          <>
            <AdminStatusFilter
              value={statusFilter}
              onChange={setStatusFilter}
              counts={statusFilterCounts}
              labels={{
                name: t("filters.status"),
                all: t("filters.statusAll"),
                active: t("filters.statusActive"),
                inactive: t("filters.statusInactive"),
              }}
            />
          <AdminRegionFilter
            value={regionFilter}
            onChange={setRegionFilter}
            valueKey="id"
          />
          </>
        }
      />

      <DataTable
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={filteredStructures}
        columns={columns}
        getRowKey={(row) => row.id}
        defaultSortColumn="name"
        preferencesKey="admin-structures"
        resetToken={buildListResetToken([
          searchQuery,
          statusFilter,
          regionFilter ?? "",
        ])}
        loadState={
          isLoading
            ? { status: "loading" }
            : error
              ? {
                  status: "error",
                  message: t("loadError"),
                  retryLabel: t("retry"),
                  onRetry: () => void refetch(),
                }
              : undefined
        }
        emptyMessage={t("empty")}
        onRowClick={(structure) => navigate(`/houses/${structure.id}`)}
        openRowLabel={(structure) => t("openRowAria", { name: structure.name })}
      />
    </PageFrame>
  );
}
